import React from 'react';
import dayjs from 'dayjs';
import type { Emitter } from 'mitt';
import { useDispatch, useSelector } from 'react-redux';
import type { Claim, Participant } from '../../api/claims/interface';
import { SessionContext } from '../../context/AppProvider';
import { FeatureKeys } from '../../data/session/interface';
import type { StoreState } from '../../data/store';
import claimsSlice, { type ClaimsListEvents, FETCH_LIMIT } from './data/logic';
export interface ClaimsHookState {
  claims: Claim[];
  filters: {
    name_or_id?: string;
    cid_date?: [string, string];
    owners?: string[];
    statuses?: Array<'Open' | 'Pending' | 'Closed'>;
  };
  sort: {
    key: string[] | string;
    order: 'ascend' | 'descend';
  } | null;
  pagination: {
    currentPage: number;
    pageSize: number;
    total: number;
  };
  isClientDossierIdUnique: boolean;
  actions: StoreState['claims']['actions'];
}

interface ClaimsHookHandler {
  fetchClaims(data: {
    filters: ClaimsHookState['filters'];
    sort: ClaimsHookState['sort'];
    currentPage: number;
    pageSize: number;
  }): void;
  onFilterChange(data: { filters: ClaimsHookState['filters'] }): void;
  stopPollingClaims(): void;
  pollClaims(bus: Emitter<ClaimsListEvents>): void;
  onSortChange(
    data: { key: string[] | string; order: 'ascend' | 'descend' } | null
  ): void;
  onPageChange(data: {
    currentPage: number;
    pageSize: number;
    sort?: ClaimsHookState['sort'];
    total: number;
  }): void;
  createSelfServiceClaim(data: {
    claimants: Participant[];
    insured?: Participant;
    clientDossierId: string;
    files: File[];
    assignee?: string;
    notes?: string;
    dossierType?: string;
  }): void;
  deleteClaim(data: { dossierId: string }): void;
  updateClaimFiles(data: {
    dossierId: string;
    files?: File[];
    notes?: string;
  }): void;
  checkClientDossierIdUnique(data: { clientDossierId: string }): void;
}

const useClaimsHook = (): [ClaimsHookState, ClaimsHookHandler] => {
  const dispatch = useDispatch();
  const { features } = React.useContext(SessionContext);
  const { claims, isClientDossierIdUnique, count, actions } = useSelector(
    (s: StoreState) => s['claims']
  );
  const { current } = useSelector((s: StoreState) => s['session']);
  const [filters, setFilters] = React.useState<ClaimsHookState['filters']>(
    () => {
      const storedFilters = sessionStorage.getItem('filters');

      if (!storedFilters) {
        if (!features[FeatureKeys.CustomerTerminology]) {
          return {};
        }

        return {
          owners: current?.email ? [current?.email] : [],
          statuses: ['Open', 'Pending'],
        };
      }

      const parsedFilters = JSON.parse(storedFilters);
      parsedFilters.cid_date = parsedFilters.cid_date?.map((d: string) =>
        dayjs(d)
      );

      return parsedFilters;
    }
  );

  const [sort, setSort] = React.useState<ClaimsHookState['sort']>(() => {
    return {
      key: features[FeatureKeys.SelfServiceView]
        ? 'updated_at'
        : ['data', 'cid_date'],
      order: 'descend',
    };
  });

  const [pagination, setPagination] = React.useState<{
    currentPage: number;
    pageSize: number;
    total: number;
  }>({
    currentPage: 1,
    pageSize: FETCH_LIMIT,
    total: count ?? 0,
  });

  // eslint-disable-next-line react-etc/prefer-usememo
  React.useEffect(() => {
    setPagination((d) => ({
      ...d,
      total: count,
    }));
  }, [count]);

  const handler = React.useMemo(
    () => ({
      fetchClaims: (data: {
        filters: ClaimsHookState['filters'];
        sort: ClaimsHookState['sort'];
        currentPage: number;
        pageSize: number;
      }) => {
        dispatch(
          claimsSlice.actions.fetchClaims({
            ...data,
            filters: {
              ...data.filters,
              cid_date: data.filters.cid_date?.map((d) => d.toString()) as [
                string,
                string
              ],
            },
          })
        );
      },
      stopPollingClaims: () => {
        dispatch({ type: 'STOP_POLLING_CLAIMS_LIST' });
      },
      pollClaims: (bus: Emitter<ClaimsListEvents>) => {
        dispatch(
          claimsSlice.actions.startPollingClaimsList({
            bus,
          })
        );
      },
      onFilterChange: (data: { filters: ClaimsHookState['filters'] }) => {
        setFilters(data.filters);
        sessionStorage.setItem('filters', JSON.stringify(data.filters));
      },
      onSortChange: (
        data: { key: string[]; order: 'ascend' | 'descend' } | null
      ) => {
        setSort(data);
      },
      onPageChange: (data: {
        currentPage: number;
        pageSize: number;
        total: number;
        sort: ClaimsHookState['sort'];
      }) => {
        setPagination((d) => ({
          ...d,
          ...data,
        }));
        if (data.sort) {
          setSort(data.sort);
        }
      },
      deleteClaim: (data: { dossierId: string }) => {
        dispatch(
          claimsSlice.actions.deleteClaim({ dossierId: data.dossierId })
        );
      },
      updateClaimFiles: (data: {
        dossierId: string;
        files?: File[];
        notes?: string;
      }) => {
        dispatch(claimsSlice.actions.updateClaimFiles(data));
      },
      createSelfServiceClaim: (data: {
        claimants: Participant[];
        insured?: Participant;
        clientDossierId: string;
        files: File[];
        assignee?: string;
        notes?: string;
        dossierType?: string;
      }) => {
        dispatch(claimsSlice.actions.createSelfServiceClaim(data));
      },
      checkClientDossierIdUnique: (data: { clientDossierId: string }) => {
        dispatch(
          claimsSlice.actions.checkClientDossierIdUnique({
            clientDossierId: data.clientDossierId,
          })
        );
      },
    }),
    []
  );

  return [
    {
      claims,
      filters,
      sort,
      pagination,
      isClientDossierIdUnique,
      actions,
    },
    handler,
  ];
};

export default useClaimsHook;
